import { registerEntityActions } from '@/actions/entityActionRegistry';
import type { EntityActionHandler } from '@/actions/entityActionRegistry';
import type { CatalogActionDialogState } from '@/components/catalog/CatalogActionDialogs';

const CATALOG_DIALOG_KEY = 'catalog.actions';

const requireDialog = (
  handlerId: string,
  openDialog?: <Payload = unknown>(key: string, payload?: Payload) => void,
) => {
  if (!openDialog) {
    throw new Error(`${handlerId} action requires dialog controls.`);
  }
  return openDialog;
};

const requireEntity = <T>(handlerId: string, entity?: T): T => {
  if (!entity) {
    throw new Error(`${handlerId} action requires an entity context.`);
  }
  return entity;
};

const createHandler: EntityActionHandler = (context) => {
  const { queryClient, notifications } = context;
  const openDialog = requireDialog('catalog.create', context.openDialog);
  openDialog<CatalogActionDialogState>(CATALOG_DIALOG_KEY, {
    type: 'create',
    queryClient,
    notifications,
  });
};

const editHandler: EntityActionHandler = (context) => {
  const { entity, queryClient, notifications } = context;
  const openDialog = requireDialog('catalog.edit', context.openDialog);
  const target = requireEntity('catalog.edit', entity);
  openDialog<CatalogActionDialogState>(CATALOG_DIALOG_KEY, {
    type: 'edit',
    entity: target,
    queryClient,
    notifications,
  });
};

const importHandler: EntityActionHandler = (context) => {
  const { queryClient, notifications } = context;
  const openDialog = requireDialog('catalog.import', context.openDialog);
  openDialog<CatalogActionDialogState>(CATALOG_DIALOG_KEY, {
    type: 'import',
    queryClient,
    notifications,
  });
};

const viewHandler: EntityActionHandler = (context) => {
  const { entity, navigate, returnSearch } = context;
  const target = requireEntity('catalog.view', entity);
  if (navigate) {
    navigate(`/catalog/${target.id}`, { state: { returnSearch } });
  }
};

// Register handlers
registerEntityActions({
  'catalog.create': createHandler,
  'catalog.edit': editHandler,
  'catalog.import': importHandler,
  'catalog.view': viewHandler,
});
